<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import WhatsappCatalogsAPI from 'dashboard/api/whatsappCatalogs';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const inboxes = useMapGetter('inboxes/getInboxes');

const inboxId = computed(() => Number(route.params.inboxId));
const inbox = computed(() =>
  (inboxes.value || []).find(item => item.id === inboxId.value)
);

const isLoading = ref(false);
const isSaving = ref(false);
const settings = ref({});
const errors = ref([]);
const discoveredCatalogs = ref([]);

const form = reactive({
  catalogId: '',
  catalogName: '',
  pixelId: '',
  shopifyStoreDomain: '',
  catalogAccessToken: '',
  clearCatalogAccessToken: false,
  isCartEnabled: true,
  isCatalogVisible: true,
});

const selectedCatalog = computed(() =>
  discoveredCatalogs.value.find(catalog => catalog.id === form.catalogId)
);

const catalogLinkUrl = computed(() => settings.value.catalog_link_url);

const integrationRoutes = computed(() => [
  {
    key: 'shopify',
    label: t('WHATSAPP_CATALOGS.INTEGRATIONS.SHOPIFY'),
    route: {
      name: 'settings_integrations_shopify',
      params: { accountId: route.params.accountId },
    },
  },
  {
    key: 'openai',
    label: t('WHATSAPP_CATALOGS.INTEGRATIONS.OPENAI'),
    route: {
      name: 'settings_applications_integration',
      params: { accountId: route.params.accountId, integration_id: 'openai' },
    },
  },
]);

const applySettings = data => {
  settings.value = data || {};
  errors.value = data?.errors || [];
  discoveredCatalogs.value = data?.catalogs || [];

  form.catalogId = data?.catalog_id || '';
  form.catalogName = data?.catalog_name || selectedCatalog.value?.name || '';
  form.pixelId = data?.pixel_id || '';
  form.shopifyStoreDomain = data?.shopify_store_domain || '';
  form.catalogAccessToken = '';
  form.clearCatalogAccessToken = false;
  form.isCartEnabled = data?.commerce_settings?.is_cart_enabled ?? true;
  form.isCatalogVisible = data?.commerce_settings?.is_catalog_visible ?? false;
};

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await WhatsappCatalogsAPI.settings(inboxId.value);
    applySettings(data);
  } catch (error) {
    errors.value = [error.response?.data?.error || error.message];
  } finally {
    isLoading.value = false;
  }
};

const useCatalog = catalog => {
  form.catalogId = catalog.id;
  form.catalogName = catalog.name || '';
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    const { data } = await WhatsappCatalogsAPI.updateSettings(inboxId.value, {
      catalog_id: form.catalogId,
      catalog_name: form.catalogName,
      pixel_id: form.pixelId,
      shopify_store_domain: form.shopifyStoreDomain,
      catalog_access_token: form.catalogAccessToken || undefined,
      clear_catalog_access_token: form.clearCatalogAccessToken,
      is_cart_enabled: form.isCartEnabled,
      is_catalog_visible: form.isCatalogVisible,
    });
    applySettings(data);
    useAlert(t('WHATSAPP_CATALOGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('WHATSAPP_CATALOGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const goBack = () => {
  router.push({ name: 'whatsapp_catalogs_index' });
};

onMounted(fetchSettings);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="sticky top-0 z-10 px-6 bg-n-surface-1 border-b border-n-weak"
    >
      <div class="w-full max-w-5xl mx-auto py-5">
        <div
          class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
        >
          <div class="min-w-0">
            <Button
              ghost
              slate
              sm
              icon="i-lucide-arrow-left"
              :label="t('WHATSAPP_CATALOGS.BACK')"
              class="mb-2"
              @click="goBack"
            />
            <h1 class="text-heading-1 text-n-slate-12">
              {{ t('WHATSAPP_CATALOGS.SETTINGS_TITLE') }}
            </h1>
            <p class="mt-1 text-body-main text-n-slate-11">
              {{ inbox?.name || t('WHATSAPP_CATALOGS.INBOX_FALLBACK') }}
            </p>
          </div>
          <Button
            teal
            icon="i-lucide-save"
            :is-loading="isSaving"
            :label="t('WHATSAPP_CATALOGS.SAVE')"
            @click="saveSettings"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-6">
        <div v-if="isLoading" class="flex items-center justify-center py-20">
          <span class="i-lucide-loader-2 animate-spin size-6 text-n-slate-10" />
        </div>

        <div v-else class="flex flex-col gap-6">
          <div
            v-if="errors.length"
            class="rounded-lg border border-n-amber-7 bg-n-amber-3 px-4 py-3 text-body-main text-n-amber-12"
          >
            <p class="font-medium">
              {{ t('WHATSAPP_CATALOGS.META_WARNING') }}
            </p>
            <p v-for="error in errors" :key="error" class="mt-1">
              {{ error }}
            </p>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <Input
              v-model="form.catalogId"
              :label="t('WHATSAPP_CATALOGS.FIELDS.CATALOG_ID')"
              :placeholder="
                t('WHATSAPP_CATALOGS.FIELDS.CATALOG_ID_PLACEHOLDER')
              "
              :message="t('WHATSAPP_CATALOGS.FIELDS.CATALOG_ID_HELP')"
            />
            <Input
              v-model="form.catalogName"
              :label="t('WHATSAPP_CATALOGS.FIELDS.CATALOG_NAME')"
              :placeholder="
                t('WHATSAPP_CATALOGS.FIELDS.CATALOG_NAME_PLACEHOLDER')
              "
            />
            <Input
              v-model="form.shopifyStoreDomain"
              :label="t('WHATSAPP_CATALOGS.FIELDS.SHOPIFY_STORE')"
              :placeholder="
                t('WHATSAPP_CATALOGS.FIELDS.SHOPIFY_STORE_PLACEHOLDER')
              "
            />
            <Input
              v-model="form.pixelId"
              :label="t('WHATSAPP_CATALOGS.FIELDS.PIXEL_ID')"
              :placeholder="t('WHATSAPP_CATALOGS.FIELDS.PIXEL_ID_PLACEHOLDER')"
            />
            <Input
              v-model="form.catalogAccessToken"
              type="password"
              :label="t('WHATSAPP_CATALOGS.FIELDS.CATALOG_ACCESS_TOKEN')"
              :placeholder="
                settings.catalog_access_token_configured
                  ? t(
                      'WHATSAPP_CATALOGS.FIELDS.CATALOG_ACCESS_TOKEN_CONFIGURED_PLACEHOLDER'
                    )
                  : t(
                      'WHATSAPP_CATALOGS.FIELDS.CATALOG_ACCESS_TOKEN_PLACEHOLDER'
                    )
              "
              :message="t('WHATSAPP_CATALOGS.FIELDS.CATALOG_ACCESS_TOKEN_HELP')"
            />
          </div>

          <div class="divide-y divide-n-weak border-y border-n-weak">
            <div class="flex items-center justify-between gap-4 py-4">
              <div>
                <p class="text-heading-3 text-n-slate-12">
                  {{
                    settings.catalog_access_token_configured
                      ? t('WHATSAPP_CATALOGS.FIELDS.CATALOG_TOKEN_CLEAR')
                      : t('WHATSAPP_CATALOGS.FIELDS.CATALOG_TOKEN_STATUS')
                  }}
                </p>
                <p class="text-body-small text-n-slate-11">
                  {{
                    settings.catalog_access_token_configured
                      ? t(
                          'WHATSAPP_CATALOGS.FIELDS.CATALOG_TOKEN_STATUS_CONFIGURED'
                        )
                      : t(
                          'WHATSAPP_CATALOGS.FIELDS.CATALOG_TOKEN_STATUS_MISSING'
                        )
                  }}
                </p>
              </div>
              <Switch
                v-if="settings.catalog_access_token_configured"
                v-model="form.clearCatalogAccessToken"
              />
            </div>
            <div class="flex items-center justify-between gap-4 py-4">
              <div>
                <p class="text-heading-3 text-n-slate-12">
                  {{ t('WHATSAPP_CATALOGS.FIELDS.CART_ENABLED') }}
                </p>
                <p class="text-body-small text-n-slate-11">
                  {{ t('WHATSAPP_CATALOGS.FIELDS.CART_ENABLED_HELP') }}
                </p>
              </div>
              <Switch v-model="form.isCartEnabled" />
            </div>
            <div class="flex items-center justify-between gap-4 py-4">
              <div>
                <p class="text-heading-3 text-n-slate-12">
                  {{ t('WHATSAPP_CATALOGS.FIELDS.CATALOG_VISIBLE') }}
                </p>
                <p class="text-body-small text-n-slate-11">
                  {{ t('WHATSAPP_CATALOGS.FIELDS.CATALOG_VISIBLE_HELP') }}
                </p>
              </div>
              <Switch v-model="form.isCatalogVisible" />
            </div>
          </div>

          <div class="grid gap-6 lg:grid-cols-[1.4fr_0.9fr]">
            <section>
              <div class="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h2 class="text-heading-2 text-n-slate-12">
                    {{ t('WHATSAPP_CATALOGS.DISCOVERED.TITLE') }}
                  </h2>
                  <p class="text-body-small text-n-slate-11">
                    {{ t('WHATSAPP_CATALOGS.DISCOVERED.DESCRIPTION') }}
                  </p>
                </div>
                <Button
                  outline
                  slate
                  sm
                  icon="i-lucide-refresh-cw"
                  :label="t('WHATSAPP_CATALOGS.REFRESH')"
                  @click="fetchSettings"
                />
              </div>

              <div
                v-if="!discoveredCatalogs.length"
                class="border-y border-n-weak py-8 text-center text-body-main text-n-slate-11"
              >
                {{ t('WHATSAPP_CATALOGS.DISCOVERED.EMPTY') }}
              </div>
              <div v-else class="divide-y divide-n-weak border-y border-n-weak">
                <div
                  v-for="catalog in discoveredCatalogs"
                  :key="catalog.id"
                  class="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between"
                >
                  <div class="min-w-0">
                    <p class="text-heading-3 text-n-slate-12">
                      {{
                        catalog.name ||
                        t('WHATSAPP_CATALOGS.DISCOVERED.UNNAMED')
                      }}
                    </p>
                    <p class="text-body-small text-n-slate-11">
                      {{ catalog.id }}
                    </p>
                  </div>
                  <Button
                    outline
                    teal
                    sm
                    icon="i-lucide-check"
                    :label="t('WHATSAPP_CATALOGS.DISCOVERED.USE')"
                    @click="useCatalog(catalog)"
                  />
                </div>
              </div>
            </section>

            <aside class="flex flex-col gap-5">
              <section>
                <h2 class="text-heading-2 text-n-slate-12">
                  {{ t('WHATSAPP_CATALOGS.STATUS.TITLE') }}
                </h2>
                <dl class="mt-3 divide-y divide-n-weak border-y border-n-weak">
                  <div class="py-3">
                    <dt class="text-label-small text-n-slate-11">
                      {{ t('WHATSAPP_CATALOGS.STATUS.WABA') }}
                    </dt>
                    <dd class="text-body-main text-n-slate-12 break-all">
                      {{
                        settings.waba_id || t('WHATSAPP_CATALOGS.EMPTY_VALUE')
                      }}
                    </dd>
                  </div>
                  <div class="py-3">
                    <dt class="text-label-small text-n-slate-11">
                      {{ t('WHATSAPP_CATALOGS.STATUS.PHONE_ID') }}
                    </dt>
                    <dd class="text-body-main text-n-slate-12 break-all">
                      {{
                        settings.phone_number_id ||
                        t('WHATSAPP_CATALOGS.EMPTY_VALUE')
                      }}
                    </dd>
                  </div>
                  <div class="py-3">
                    <dt class="text-label-small text-n-slate-11">
                      {{ t('WHATSAPP_CATALOGS.STATUS.CATALOG_LINK') }}
                    </dt>
                    <dd class="text-body-main text-n-slate-12 break-all">
                      <a
                        v-if="catalogLinkUrl"
                        class="text-n-brand hover:underline"
                        :href="catalogLinkUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ catalogLinkUrl }}
                      </a>
                      <span v-else>{{
                        t('WHATSAPP_CATALOGS.EMPTY_VALUE')
                      }}</span>
                    </dd>
                  </div>
                </dl>
              </section>

              <section>
                <h2 class="text-heading-2 text-n-slate-12">
                  {{ t('WHATSAPP_CATALOGS.INTEGRATIONS.TITLE') }}
                </h2>
                <p class="mt-1 text-body-small text-n-slate-11">
                  {{ t('WHATSAPP_CATALOGS.INTEGRATIONS.DESCRIPTION') }}
                </p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <router-link
                    v-for="integration in integrationRoutes"
                    :key="integration.key"
                    :to="integration.route"
                  >
                    <Button
                      outline
                      slate
                      sm
                      icon="i-lucide-arrow-up-right"
                      :label="integration.label"
                    />
                  </router-link>
                </div>
              </section>
            </aside>
          </div>
        </div>
      </div>
    </main>
  </section>
</template>
